<template>
  <ul :class="classes">
    <slot name="list-prepend" />
    <li v-for="item in items" :key="item.id" :class="getItemClass(item)">
      <AppMenuNavItem
        :disabled="disabled"
        :item="item"
        :layout="layout"
        :list-children="listChildren"
        :list-type="listType"
        @item-click="$emit('item-click', $event)"
        @set-active-additional-menu="setActiveAdditionalMenu"
      >
        <template v-if="isMenuExpanded" #[objectivesItemAppendSlotName]>
          <portal-target name="menu-objectives-link" />
        </template>

        <template v-if="isMenuExpanded" #[organizationItemLabelSlotName]="{ keyLabel }">
          <i18n-t :keypath="keyLabel" scope="global">
            <template #break>
              <br />
            </template>
          </i18n-t>
        </template>

        <!-- EXAMPLE of using label slot with badge -->
        <!-- <template #[checkinsSettingsItemLabelSlotName]="{ label }">
          {{ label }}
          <AppBadge :content="$t('dashboard.new')" class="amnl-ListItemBadge" inline is-uppercase />
        </template>-->
      </AppMenuNavItem>
      <AppMenuNavList
        v-if="
          item.children &&
          !item.showChildrenLikeAdditionalMenu &&
          layout === APP_MENU_NAV_LAYOUTS.VERTICAL
        "
        :disabled="disabled"
        :items="item.children"
        :list-type="listType"
        list-children
        @item-click="$emit('item-click', $event)"
      />
    </li>
  </ul>
</template>

<script>
import { camelCase } from 'lodash'
import { defineComponent } from 'vue'

import {
  APP_MENU_NAV_LAYOUTS,
  getMenuItemAppendSlotName,
  getMenuItemLabelSlotName,
  useAppMenu
} from '@/utils/app-menu'
import { menuItemsNames } from '@/utils/menu-items'
import { includesValue } from '@/utils/prop-validators'

import AppMenuNavItem from '@/components/AppMenu/AppMenuNavItem'
import AppMenuNavList from '@/components/AppMenu/AppMenuNavList'

export default defineComponent({
  name: 'AppMenuNavList',

  components: { AppMenuNavItem, AppMenuNavList },

  props: {
    items: {
      type: Array,
      required: true
    },

    listChildren: {
      type: Boolean
    },

    listType: {
      type: String,
      default: 'default',
      validator: v => ['default', 'additionalMenu'].includes(v)
    },

    layout: {
      type: String,
      default: APP_MENU_NAV_LAYOUTS.VERTICAL,
      validator: v => includesValue(v, APP_MENU_NAV_LAYOUTS)
    },

    disabled: {
      type: Boolean
    }
  },

  emits: { 'set-active-additional-menu': null, 'item-click': null },

  setup() {
    const { isMenuExpanded, isMenuCollapsed } = useAppMenu()

    return {
      isMenuExpanded,
      isMenuCollapsed
    }
  },

  computed: {
    // checkinsSettingsItemLabelSlotName() {
    //   return getMenuItemLabelSlotName(menuItemsNames.SETTINGS.children.CHECK_INS.name)
    // },

    organizationItemLabelSlotName() {
      return getMenuItemLabelSlotName(menuItemsNames.SETTINGS.children.ORGANIZATION.name)
    },

    objectivesItemAppendSlotName() {
      return getMenuItemAppendSlotName(menuItemsNames.OBJECTIVES.name)
    },

    // ...mapState('pluginOptions', {
    //   isPluginAdmin: state => state.isPluginAdmin
    // }),

    APP_MENU_NAV_LAYOUTS: () => APP_MENU_NAV_LAYOUTS,

    classes() {
      const { listChildren, layout } = this
      return {
        'amnl-List': true,
        'amnl-List-children': listChildren,
        [`amnl-List-${layout}`]: true
      }
    }
  },

  methods: {
    setActiveAdditionalMenu(menuItemName) {
      this.$emit('set-active-additional-menu', menuItemName)
    },

    getItemClass(item) {
      return {
        'amnl-List_Item': true,
        'amnl-List_Item-with-bottom-divider': item.hasBottomDivider,
        [`amnl-List_Item-${camelCase(item.name)}`]: true,
        // we should hide items using v-show or styles instead of v-if
        // to avoid destroying nested menus
        'amnl-List_Item-invisible': item.showChildrenLikeAdditionalMenu && this.isMenuCollapsed
      }
    }
  }
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/app-menu';

$bottom-offset: 33px;

.amnl-List {
  list-style: none;
  margin: 0;
  padding: 0;

  &:not(&-children) {
    display: flex;
    gap: var(--gap, 8px);
  }

  &:not(&-horizontal) {
    flex-direction: column;
  }

  &-children {
    &:not(&:empty) {
      padding: 4px 4px 8px 4px;
    }
  }

  &-horizontal {
    align-items: center;
    justify-content: center;
  }
}

.amnl-List_Item {
  &-with-bottom-divider {
    position: relative;
    margin-bottom: $bottom-offset;

    &:after {
      content: '';
      position: absolute;
      bottom: calc(#{$bottom-offset / 2 - 0.5} * -1);
      width: 100%;
      height: 1px;
      border-radius: 1px;
      background: $grey-2-next;
      pointer-events: none;
    }
  }

  &-invisible {
    display: none;
  }
}

.amnl-ListItemBadge {
  --color: var(--grade-low-color-next);
  --badge-size: 12px;
  --padding: 2px 4px;
  vertical-align: middle;
  margin-left: 4px;
  margin-bottom: 2px;
  z-index: 0;
}
</style>
